import { useEffect, useMemo, useState } from 'react';
import { rutaParaResponder } from '../lib/emailThreads';
import { localeUI } from '../lib/format';
import {
  Banknote, CalendarDays, ChevronDown, ChevronsDownUp, ExternalLink, FileText, Flag, Hand, History, Landmark, Mail,
  Maximize2, MessageSquare, Paperclip, PenLine, Phone, Receipt, Ruler, Settings,
  type LucideIcon,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { MessageBody } from '../lib/messageBody';
import { EmailCard, type EmailLike } from './email/EmailThread';
import { FileViewerModal } from './FileViewerModal';

/**
 * TODO LO QUE PASÓ CON ESTE CLIENTE, EN UNA SOLA LISTA.
 *
 * Cada departamento tiene su propia pestaña y su propio timeline, y cada uno contesta muy bien SU
 * pregunta. La que nadie podía contestar sin recorrer seis pestañas es la que se hace todos los
 * días: *"¿qué pasó con este cliente?"*.
 *
 * Junta 13 fuentes —etapas, notas, updates de Monday, emails, SMS, llamadas, archivos, estimados,
 * cheques, invoices, pagos, inspecciones, contrato/COC/welcome call— en orden cronológico.
 *
 * Los filtros son por TIPO y no por departamento: cuando alguien busca "¿le mandamos el cheque?"
 * piensa en cheques, no en Payments.
 */
type Item = {
  id: string;
  tipo: 'etapa' | 'nota' | 'monday' | 'email' | 'sms' | 'llamada' | 'archivo' | 'estimado'
    | 'cheque' | 'pago' | 'invoice' | 'cita' | 'contrato' | 'coc' | 'welcome';
  at: string; titulo: string; detalle: string | null; autor: string | null;
  depto: string | null; montoCents: number | null; refId: string | null;
};

/** Ícono y color por tipo: el ojo tiene que poder saltar al tipo que busca sin leer. */
const ESTILO: Record<Item['tipo'], { ico: LucideIcon; color: string }> = {
  etapa: { ico: Flag, color: '#7c3aed' },
  nota: { ico: MessageSquare, color: '#0d9488' },
  monday: { ico: Settings, color: '#4338ca' },
  email: { ico: Mail, color: '#2563eb' },
  sms: { ico: MessageSquare, color: '#0284c7' },
  llamada: { ico: Phone, color: '#0284c7' },
  archivo: { ico: Paperclip, color: '#6b7280' },
  estimado: { ico: Ruler, color: '#b45309' },
  cheque: { ico: Landmark, color: '#166534' },
  pago: { ico: Banknote, color: '#166534' },
  invoice: { ico: Receipt, color: '#166534' },
  cita: { ico: CalendarDays, color: '#db2777' },
  contrato: { ico: PenLine, color: '#7c3aed' },
  coc: { ico: FileText, color: '#166534' },
  welcome: { ico: Hand, color: '#b45309' },
};

// Los MONTOS van siempre en en-US (es una empresa de EE. UU.; Juan-Jared 2026-10-20): el idioma solo cambia las fechas.
const usd = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dia = (iso: string) => new Date(iso).toLocaleDateString(localeUI(), { year: 'numeric', month: 'short', day: 'numeric' });
const hora = (iso: string) => new Date(iso).toLocaleTimeString(localeUI(), { hour: 'numeric', minute: '2-digit' });

export function ClaimHistory({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [items, setItems] = useState<Item[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tipos, setTipos] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  // DESPLEGAR EN EL LUGAR (pedido del dueño 2026-09-29): clic en la fila la abre ahí mismo con el
  // contenido COMPLETO (la nota entera, el correo, la llamada con su resumen, el archivo) y otro clic
  // la recoge. Antes el título te sacaba de la pantalla y la fila abría un modal con el mismo resumen
  // recortado. Se pueden tener varias abiertas a la vez para comparar.
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const alternarFila = (id: string) => setAbiertos((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  /** A dónde vive el ORIGINAL de cada evento: clic en el título = ir a la cosa, no a un resumen. */
  const destino = (i: Item): string | null => {
    switch (i.tipo) {
      case 'email': return i.refId ? `/dept/${i.depto || 'unassigned'}/inbox?email=${i.refId}` : `/claims/${claimId}?tab=email`;
      case 'nota': return `/claims/${claimId}?tab=${i.depto || 'overview'}${i.refId ? `#msg-${i.refId}` : ''}`;
      case 'sms': return `/claims/${claimId}?tab=sms`;
      case 'llamada': return `/claims/${claimId}?tab=calllog`;
      case 'archivo': return `/claims/${claimId}?tab=files`;
      case 'estimado': return `/claims/${claimId}?tab=supplements`;
      case 'cheque': case 'pago': case 'invoice': return `/claims/${claimId}?tab=finance`;
      case 'cita': return `/claims/${claimId}?tab=scheduling`;
      case 'contrato': case 'welcome': return `/claims/${claimId}?tab=intake`;
      case 'coc': return `/claims/${claimId}?tab=coc`;
      default: return null; // etapa y monday no tienen un "original" navegable
    }
  };

  useEffect(() => {
    let vivo = true;
    setItems(null);
    api.get<{ items: Item[] }>(`/api/claims/${claimId}/history`)
      .then((r) => { if (vivo) { setItems(r.items); setErr(null); } })
      .catch((e) => { if (vivo) { setItems([]); setErr(e instanceof ApiError ? e.message : 'Error'); } });
    return () => { vivo = false; };
  }, [claimId]);

  const conteo = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of items ?? []) m.set(i.tipo, (m.get(i.tipo) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [items]);

  const visibles = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (items ?? []).filter((i) => (tipos.size === 0 || tipos.has(i.tipo))
      && (!ql || `${i.titulo} ${i.detalle ?? ''} ${i.autor ?? ''}`.toLowerCase().includes(ql)));
  }, [items, tipos, q]);

  const alternar = (tp: string) => setTipos((s) => {
    const n = new Set(s);
    if (n.has(tp)) n.delete(tp); else n.add(tp);
    return n;
  });

  return (
    <div className="chist" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-base)' }}><History size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hist.title')}</h3>
        {items && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{visibles.length}/{items.length}</span>}
        <span style={{ flex: 1 }} />
        <input className="input sm" style={{ maxWidth: 240 }} placeholder={t('hist.search')} value={q} onChange={(e) => setQ(e.target.value)} />
        {tipos.size > 0 && <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => setTipos(new Set())}>{t('hist.clear')}</button>}
        {abiertos.size > 0 && (
          <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => setAbiertos(new Set())}>
            <ChevronsDownUp size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hist.collapseAll')}
          </button>
        )}
      </div>

      {/* Filtros por tipo, con su conteo: además de filtrar, dicen de qué está hecha la historia. */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {conteo.map(([tp, n]) => {
          const st = ESTILO[tp as Item['tipo']];
          const on = tipos.has(tp);
          const Ico = st.ico;
          return (
            <button key={tp} className="btn ghost sm" onClick={() => alternar(tp)}
              style={{ fontSize: 'var(--fs-2xs)', padding: '2px 8px', border: `1px solid ${on ? st.color : 'var(--line)'}`,
                background: on ? st.color : 'transparent', color: on ? '#fff' : 'var(--ink)' }}>
              <Ico size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t(`hist.t_${tp}`)} {n}
            </button>
          );
        })}
      </div>

      {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{err}</div>}
      {!items ? <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('common.loading')}</div>
        : !visibles.length ? <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('hist.empty')}</div> : (
          <div style={{ display: 'grid', gap: 2 }}>
            {visibles.map((i, idx) => {
              const st = ESTILO[i.tipo];
              const Ico = st.ico;
              // Separador de día: la historia se lee por jornadas, no por filas sueltas.
              const nuevoDia = idx === 0 || dia(visibles[idx - 1]!.at) !== dia(i.at);
              return (
                <div key={i.id}>
                  {nuevoDia && (
                    <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', margin: '10px 0 4px', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {dia(i.at)}
                    </div>
                  )}
                  {/* Fila = resumen compacto. Clic → se DESPLIEGA debajo con todo; otro clic → se recoge. */}
                  <div className={`chist-item${abiertos.has(i.id) ? ' open' : ''}`} style={{ borderLeftColor: st.color }}>
                    <div className="chist-row" role="button" tabIndex={0} aria-expanded={abiertos.has(i.id)}
                      onClick={() => alternarFila(i.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alternarFila(i.id); } }}
                      style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 8px', cursor: 'pointer' }}>
                      <span style={{ fontSize: 'var(--fs-sm)', color: st.color, display: 'inline-flex', marginTop: 2 }}><Ico size={14} strokeWidth={1.75} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="chist-titlebox" style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap', minWidth: 0 }}>
                          <span className="chist-title" style={{ fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)' }}>{i.titulo}</span>
                          {i.montoCents != null && (
                            <b style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)', color: i.montoCents < 0 ? 'var(--red)' : 'var(--green)' }}>{usd(i.montoCents)}</b>
                          )}
                          {i.depto && <span className="badge chist-depto" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--card)' }}>{i.depto}</span>}
                        </div>
                        {i.detalle && !abiertos.has(i.id) && (
                          <div className="muted chist-det" style={{ fontSize: 'var(--fs-2xs)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.detalle}</div>
                        )}
                      </div>
                      <span className="muted" style={{ fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>
                        <span className="chist-autor">{i.autor ? `${i.autor} · ` : ''}</span>{hora(i.at)}
                      </span>
                      <ChevronDown size={14} strokeWidth={1.75} className="chist-chev" aria-hidden />
                    </div>
                    {abiertos.has(i.id) && <DetalleFila claimId={claimId} item={i} destino={destino(i)} onCerrar={() => alternarFila(i.id)} />}
                  </div>
                </div>
              );
            })}
          </div>
        )}

    </div>
  );
}

type Detalle =
  | { tipo: 'nota'; body: string; department: string | null; attachments: Array<{ name: string }>; email_id: string | null; recording_id: string | null }
  | { tipo: 'monday'; body: string }
  | { tipo: 'sms'; body: string; direction: string | null; from_number: string | null; to_number: string | null; attachments: Array<{ name: string }> }
  | { tipo: 'llamada'; direction: string | null; from: string | null; to: string | null; party: string | null; duration_sec: number | null; result: string | null;
      recording_id: string | null; ai_summary: string | null; ai_transcript: string | null }
  | { tipo: 'archivo'; file_id: string; file_name: string | null; mime_type: string | null; doc_label: string | null; ai_summary: string | null };

/** Tipos que traen más de lo que ya vino en la lista (la nota entera, el correo, la llamada…). */
const CON_DETALLE = new Set<Item['tipo']>(['nota', 'monday', 'sms', 'llamada', 'archivo', 'email']);
const esImagen = (n: string | null | undefined) => /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(n ?? '');
const esVideo = (n: string | null | undefined) => /\.(mp4|mov|m4v|webm|ogv)$/i.test(n ?? '');
const esAudio = (n: string | null | undefined) => /\.(mp3|wav|m4a|aac|ogg|oga|weba|amr)$/i.test(n ?? '');
const esPdf = (n: string | null | undefined, mime?: string | null) => mime === 'application/pdf' || /\.pdf$/i.test(n ?? '');

const caja: React.CSSProperties = { fontSize: 'var(--fs-sm)', overflowWrap: 'anywhere', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 10px' };
const etiqueta: React.CSSProperties = { fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--muted)' };

/**
 * Lo que se ve al DESPLEGAR una fila de la historia: el contenido completo, en el lugar. Se pide al
 * abrir (la lista trae cientos de filas con el resumen; el detalle se trae de a una). Todo se queda
 * en esta pantalla: los adjuntos se ven acá (imagen, PDF, audio) y el archivo grande abre en el
 * visor encima, no en otra ruta. "Abrir el original" sigue abajo, chico, para quien lo quiera.
 */
function DetalleFila({ claimId, item, destino, onCerrar }: { claimId: string; item: Item; destino: string | null; onCerrar: () => void }) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [det, setDet] = useState<Detalle | null>(null);
  const [email, setEmail] = useState<EmailLike | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cargando, setCargando] = useState(CON_DETALLE.has(item.tipo));
  const [archivoUrl, setArchivoUrl] = useState<string | null>(null);
  const [visor, setVisor] = useState(false);

  useEffect(() => {
    if (!CON_DETALLE.has(item.tipo)) return;
    let vivo = true;
    const traer = async () => {
      try {
        if (item.tipo === 'email') {
          if (!item.refId) return;
          const r = await api.get<{ email: EmailLike }>(`/api/claims/${claimId}/emails/${item.refId}`);
          if (vivo) setEmail(r.email);
          return;
        }
        const r = await api.get<{ item: Detalle }>(`/api/claims/${claimId}/history/${item.id}`);
        if (!vivo) return;
        setDet(r.item);
        // La nota de "Email sent…" trae el correo real: se muestra entero debajo.
        if (r.item.tipo === 'nota' && r.item.email_id) {
          const e = await api.get<{ email: EmailLike }>(`/api/claims/${claimId}/emails/${r.item.email_id}`).catch(() => null);
          if (vivo && e) setEmail(e.email);
        }
        if (r.item.tipo === 'archivo') {
          const u = await api.get<{ url: string }>(`/api/files/${r.item.file_id}/url`).catch(() => null);
          if (vivo && u) setArchivoUrl(u.url);
        }
      } catch (e) {
        if (vivo) setErr(e instanceof ApiError ? e.message : 'Error');
      } finally {
        if (vivo) setCargando(false);
      }
    };
    void traer();
    return () => { vivo = false; };
  }, [claimId, item.id, item.tipo, item.refId]);

  const adjuntosDeNota = (atts: Array<{ name: string }>, mid: string) => atts.length > 0 && (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={etiqueta}><Paperclip size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hist.attachments')} ({atts.length})</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {atts.map((a, idx) => {
          const u = `/api/claims/${claimId}/chat/${mid}/attachment?i=${idx}`;
          if (esImagen(a.name)) return <a key={idx} href={u} target="_blank" rel="noreferrer" title={a.name}><img src={u} alt={a.name} style={{ maxWidth: 320, maxHeight: 240, borderRadius: 8, border: '1px solid var(--line)', display: 'block', objectFit: 'cover' }} /></a>;
          if (esVideo(a.name)) return <video key={idx} controls preload="metadata" src={u} style={{ maxWidth: 360, width: '100%', borderRadius: 8, background: '#000' }} />;
          if (esAudio(a.name)) return <audio key={idx} controls preload="none" src={u} style={{ height: 36, width: 280 }} />;
          return <a key={idx} className="btn ghost sm" href={u} target="_blank" rel="noreferrer"><FileText size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {a.name}</a>;
        })}
      </div>
    </div>
  );

  let cuerpo: React.ReactNode = null;
  if (cargando) cuerpo = <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('common.loading')}</div>;
  else if (err) cuerpo = <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{t('hist.detailError')} · {err}</div>;
  else if (item.tipo === 'email') {
    cuerpo = email ? <EmailCard message={email} defaultOpen showSubject onReply={(m) => navigate(rutaParaResponder(claimId, m))} /> : <div style={caja}>{item.detalle}</div>;
  } else if (det?.tipo === 'nota') {
    cuerpo = (
      <>
        {det.body.trim() && <MessageBody body={det.body} lang={lang} style={caja} />}
        {adjuntosDeNota(det.attachments, item.refId ?? item.id.slice(1))}
        {det.recording_id && <audio controls preload="none" src={`/api/recordings/${det.recording_id}/url`} style={{ height: 36, width: 320 }} />}
        {email && <EmailCard message={email} defaultOpen showSubject onReply={(m) => navigate(rutaParaResponder(claimId, m))} />}
      </>
    );
  } else if (det?.tipo === 'monday') {
    cuerpo = <MessageBody body={det.body} lang={lang} style={caja} />;
  } else if (det?.tipo === 'sms') {
    cuerpo = (
      <>
        {(det.from_number || det.to_number) && (
          <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{det.from_number ?? '—'} → {det.to_number ?? '—'}</div>
        )}
        <div style={{ ...caja, whiteSpace: 'pre-wrap' }}>{det.body}</div>
        {det.attachments.length > 0 && (
          <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}><Paperclip size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {det.attachments.map((a) => a.name).join(', ')}</div>
        )}
      </>
    );
  } else if (det?.tipo === 'llamada') {
    const min = det.duration_sec != null ? `${Math.floor(det.duration_sec / 60)}:${String(det.duration_sec % 60).padStart(2, '0')}` : null;
    cuerpo = (
      <>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 'var(--fs-xs)' }}>
          <span><span style={etiqueta}>{t('hist.from')}</span> {det.from ?? '—'}</span>
          <span><span style={etiqueta}>{t('hist.to')}</span> {det.to ?? '—'}</span>
          {min && <span><span style={etiqueta}>{t('hist.duration')}</span> {min}</span>}
          {det.result && <span><span style={etiqueta}>{t('hist.result')}</span> {det.result}</span>}
        </div>
        {det.recording_id
          ? <audio controls preload="none" src={`/api/recordings/${det.recording_id}/url`} style={{ height: 36, width: 320, maxWidth: '100%' }} />
          : <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('hist.noRecording')}</div>}
        {det.ai_summary && (
          <div style={{ display: 'grid', gap: 4 }}>
            <div style={etiqueta}>{t('hist.aiSummary')}</div>
            <div style={{ ...caja, whiteSpace: 'pre-wrap' }}>{det.ai_summary}</div>
          </div>
        )}
        {det.ai_transcript && (
          <details>
            <summary style={{ ...etiqueta, cursor: 'pointer' }}>{t('hist.transcript')}</summary>
            <div style={{ ...caja, whiteSpace: 'pre-wrap', maxHeight: 360, overflow: 'auto', marginTop: 6 }}>{det.ai_transcript}</div>
          </details>
        )}
      </>
    );
  } else if (det?.tipo === 'archivo') {
    cuerpo = (
      <>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--fs-xs)' }}>
          <FileText size={14} strokeWidth={1.75} />
          <b style={{ overflowWrap: 'anywhere' }}>{det.file_name ?? '—'}</b>
          {det.doc_label && <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{det.doc_label}</span>}
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={() => setVisor(true)}><Maximize2 size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hist.fullScreen')}</button>
        </div>
        {det.ai_summary && <div style={{ ...caja, whiteSpace: 'pre-wrap' }}>{det.ai_summary}</div>}
        {archivoUrl && (esImagen(det.file_name)
          ? <img src={archivoUrl} alt={det.file_name ?? ''} style={{ maxWidth: '100%', maxHeight: 520, borderRadius: 8, border: '1px solid var(--line)', objectFit: 'contain', justifySelf: 'start' }} />
          : esPdf(det.file_name, det.mime_type)
            ? <iframe title={det.file_name ?? 'file'} src={archivoUrl} style={{ width: '100%', height: 520, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--card)' }} />
            : esVideo(det.file_name) ? <video controls preload="metadata" src={archivoUrl} style={{ maxWidth: 520, width: '100%', borderRadius: 8, background: '#000' }} />
              : esAudio(det.file_name) ? <audio controls preload="none" src={archivoUrl} style={{ height: 36, width: 320 }} /> : null)}
        {visor && <FileViewerModal fileId={det.file_id} title={det.file_name ?? ''} onClose={() => setVisor(false)} />}
      </>
    );
  } else {
    // Etapa, estimado, cheque, invoice, pago, cita, contrato, COC, welcome: la lista ya lo trae entero.
    cuerpo = (
      <>
        {item.detalle ? <div style={{ ...caja, whiteSpace: 'pre-wrap' }}>{item.detalle}</div> : null}
        {item.montoCents != null && <b style={{ fontFamily: 'var(--font-mono)', color: item.montoCents < 0 ? 'var(--red)' : 'var(--green)' }}>{usd(item.montoCents)}</b>}
        {!item.detalle && item.montoCents == null && <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('hist.nothingMore')}</div>}
      </>
    );
  }

  return (
    <div className="chist-detail">
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
        {dia(item.at)} · {hora(item.at)}{item.autor ? ` · ${item.autor}` : ''}{item.depto ? ` · ${item.depto}` : ''}
      </div>
      {cuerpo}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn ghost sm" onClick={onCerrar}><ChevronsDownUp size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('hist.collapse')}</button>
        <span style={{ flex: 1 }} />
        {destino && (
          <Link to={destino} className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
            {t('hist.open')} <ExternalLink size={11} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />
          </Link>
        )}
      </div>
    </div>
  );
}
